// Shell-level "which project are we viewing?" dropdown. Lives in TopNav, drives
// SelectedProjectContext. A project is always selected — every CRM screen
// scopes its lists to that project.

import React, { useEffect, useRef, useState, useMemo } from "react";
import { ChevronDown, FolderKanban, Search, Check } from "lucide-react";
import { useSelectedProject } from "../contexts/SelectedProjectProvider";
import { formatProject } from "../hooks/useProjects";

export default function ProjectSelector() {
  const { projectId, setProjectId, project, projects, isLoading } = useSelectedProject();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return projects;
    return projects.filter((p) => formatProject(p).toLowerCase().includes(s));
  }, [projects, q]);

  const label = isLoading
    ? "Loading…"
    : project ? formatProject(project) : "Select project…";

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`tg-tool ${open ? "is-open" : ""}`}
        title={label}
      >
        <FolderKanban size={13} />
        <span className="tg-tool-value">{label}</span>
        <ChevronDown size={13} />
      </button>

      {open && (
        <div
          style={{
            position: "absolute", top: "calc(100% + 6px)", right: 0,
            width: 280, maxHeight: 360, overflowY: "auto",
            background: "var(--color-bg-surface)",
            border: "1px solid var(--color-border-subtle)",
            borderRadius: 8,
            boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
            zIndex: 100,
            padding: 6,
          }}
        >
          <div style={{ position: "relative", marginBottom: 4 }}>
            <Search size={12} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", opacity: 0.5 }} />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search projects…"
              style={{
                width: "100%", height: 30, paddingLeft: 26, paddingRight: 8,
                fontSize: 12, border: "1px solid var(--color-border-subtle)",
                background: "var(--color-bg-base)", borderRadius: 7,
                color: "var(--color-text-primary)", outline: "none",
              }}
            />
          </div>

          {filtered.length === 0 && (
            <div style={{ padding: "10px 8px", fontSize: 12, color: "var(--color-text-secondary)" }}>
              No projects match.
            </div>
          )}
          {filtered.map((p) => (
            <Row
              key={String(p._id)}
              label={formatProject(p)}
              sub={p.status || ""}
              active={String(projectId) === String(p._id)}
              onClick={() => { setProjectId(String(p._id)); setOpen(false); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ label, sub, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 8, width: "100%",
        padding: "8px 8px", border: "none",
        background: active ? "var(--crm-primary-soft)" : "transparent",
        color: "var(--color-text-primary)",
        cursor: "pointer", borderRadius: 6,
        textAlign: "left",
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "var(--color-bg-base)"; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
    >
      <FolderKanban size={13} style={{ color: "var(--crm-primary)", flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {label}
        </div>
        {sub && (
          <div style={{ fontSize: 11, color: "var(--color-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {sub}
          </div>
        )}
      </div>
      {active && <Check size={13} style={{ color: "var(--crm-primary)" }} />}
    </button>
  );
}
